import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsSectionCard } from "@/features/seller-portal/SettingsSectionCard";
import { SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useUpdateSellerProfile } from "@/features/seller-portal/hooks";
import type { MyShopProfile } from "@/features/seller-portal/api";
import { languageCode } from "@/lib/language-code";

const LANGS = ["ru", "ky", "en"] as const;

export function StorefrontDetailsSection({
  profile,
  readOnly,
}: {
  profile: MyShopProfile;
  readOnly: boolean;
}): ReactElement {
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const update = useUpdateSellerProfile();

  const [hours, setHours] = useState(profile.profile.operating_hours ?? "");
  // SCRUM-175: normalize on read too, not just on write — some seller rows
  // carry pre-existing uppercase codes (e.g. seeded {RU,KY}). Without this,
  // `languages.includes(l)` (l is always lowercase, from LANGS below) never
  // matches a stored "RU", so that checkbox renders unchecked even though
  // the language IS already selected — toggling it then appends a lowercase
  // duplicate alongside the untouched uppercase original.
  const [languages, setLanguages] = useState<string[]>(() =>
    Array.from(new Set(profile.profile.languages.map((l) => l.toLowerCase()))),
  );
  const [warranty, setWarranty] = useState(profile.profile.warranty);
  const [years, setYears] = useState(profile.profile.years_in_business?.toString() ?? "");
  const [district, setDistrict] = useState(profile.profile.district ?? "");

  const toggleLang = (l: string): void =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const onSave = (): void => {
    update.mutate(
      {
        operating_hours: hours || undefined,
        // Defensive normalization at the write boundary too, so this stays
        // correct even if `languages` is ever seeded from somewhere else.
        languages: Array.from(new Set(languages.map((l) => l.toLowerCase()))),
        warranty,
        years_in_business: years ? Number(years) : undefined,
        district: district || undefined,
      },
      {
        onSuccess: () => toast.success(t("settings.saved")),
        onError: () => toast.error(t("settings.saveError")),
      },
    );
  };

  return (
    <SettingsSectionCard
      title={t("settings.sections.storefront")}
      testid="settings-section-storefront"
      onSave={onSave}
      saving={update.isPending}
      disabled={readOnly}
    >
      <div>
        <Label htmlFor="set-hours">{t("settings.storefront.hours")}</Label>
        <Input
          id="set-hours"
          data-testid="set-hours"
          value={hours}
          disabled={readOnly}
          onChange={(e) => setHours(e.target.value)}
          placeholder={t("settings.storefront.hoursPlaceholder")}
        />
      </div>
      <fieldset>
        <legend className="text-sm text-muted-foreground">{t("settings.storefront.languages")}</legend>
        <div className="mt-1 flex gap-3">
          {LANGS.map((l) => (
            <label key={l} className="flex items-center gap-1 text-sm text-foreground">
              <input
                type="checkbox"
                data-testid={`set-lang-${l}`}
                disabled={readOnly}
                checked={languages.includes(l)}
                onChange={() => toggleLang(l)}
              />
              {languageCode(l)}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          data-testid="set-warranty"
          disabled={readOnly}
          checked={warranty}
          onChange={(e) => setWarranty(e.target.checked)}
        />
        {t("settings.storefront.warranty")}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="set-years">{t("settings.storefront.years")}</Label>
          <Input
            id="set-years"
            data-testid="set-years"
            inputMode="numeric"
            value={years}
            disabled={readOnly}
            onChange={(e) => setYears(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="set-district">{t("settings.storefront.district")}</Label>
          <Input
            id="set-district"
            data-testid="set-district"
            value={district}
            disabled={readOnly}
            onChange={(e) => setDistrict(e.target.value)}
          />
        </div>
      </div>
    </SettingsSectionCard>
  );
}
